import { Component, inject, signal } from '@angular/core';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import L from 'leaflet';
import geojson from 'geojson';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { form, FormField, required, submit, validate } from '@angular/forms/signals';
import { MatSelectModule } from '@angular/material/select';

@Component({
  selector: 'app-leaflet-geometry-form',
  templateUrl: './geometry.component.html',
  styleUrls: ['./geometry.component.scss'],
  imports: [
    FormField,
    MatInputModule,
    MatSelectModule,
    MatDialogModule,
    MatButtonModule,
  ],
})
export class LeafletGeometryFormComponent {
  dialogRef = inject<MatDialogRef<LeafletGeometryFormComponent>>(MatDialogRef);

  geometryForm = form<{
    type: 'Point' | 'LineString' | 'Polygon';
    values: string;
  }>(signal({
    type: 'Point',
    values: '',
  }), (schemaPath) => {
    required(schemaPath.values)

    validate(schemaPath.values, ({ value }) => {
      const text = value();
      const shape = this.geometryForm.type().value();

      try {
        this.parse(shape, text);
      } catch (err) {
        return {
          kind: 'geometry',
          message: String(err),
        };
      }

      return null;
    });
  });

  public submit(event: Event) {
    event.preventDefault();

    submit(this.geometryForm, async () => {
      const shape = this.geometryForm.type().value();
      const text = this.geometryForm.values().value();

      try {
        const objects = this.parse(shape, text);
        this.dialogRef.close(objects);
      } catch (err) {
        console.error(err);
      }
    });
  }

  private parse(type: string, text: string): geojson.Geometry[] {
    const objects: geojson.GeometryObject[] = [];

    const lines = text.split('\n').map(v => v.trim());
    for (const line of lines) {
      const pairs = line.split(',').map(v => v.trim());

      const positions = this.parseLine(line);
      let geometry: geojson.Geometry;
      switch (type) {
        case "Point": {
          if (pairs.length > 1) {
            throw `Too many points: '${line}'`;
          }

          geometry = {
            type: 'Point',
            coordinates: positions[0],
          } as geojson.Point;

          break;
        }
        case "LineString": {
          if (pairs.length < 2) {
            throw `Too few points: '${line}'`;
          }

          geometry = {
            type: 'LineString',
            coordinates: positions,
          } as geojson.LineString;

          break;
        }
        case "Polygon": {
          if (pairs.length < 2) {
            throw `Too few points: '${line}'`;
          } else if (pairs[0] != pairs[pairs.length - 1]) {
            throw `Last point does not match the first: '${line}'`;
          }

          geometry = {
            type: 'Polygon',
            coordinates: [positions],
          } as geojson.Polygon;

          break;
        }
        default:
          throw `Invalid geometry type: ${type}`;
      }

      objects.push(geometry);
    }

    return objects;
  }

  private parseLine(line: string): geojson.Position[] {
    const pairs = line.split(',').map(v => v.trim());

    const positions = [] as geojson.Position[];
    for (const pair of pairs) {
      const coordinatePair = pair.split(' ');
      if (coordinatePair.length != 2) {
        throw `Invalid number of coordinates: '${line}'`;
      }

      const lat = Number(coordinatePair[0]);
      const lng = Number(coordinatePair[1]);

      if (Math.abs(lat) > 90) {
        throw `Latitude must be within -90 to 90 range: '${line}'`;
      }

      if (Math.abs(lng) > 180) {
        throw `Longitude must be within -180 to 180 range: '${line}'`;
      }

      try {
        const point = new L.LatLng(lat, lng);
        positions.push(L.GeoJSON.latLngToCoords(point));
      } catch (err) {
        console.error(err);
        throw `Invalid latitude/longitude: '${line}'`;
      }
    }

    return positions;
  }
}
